import { useEffect, useRef } from "react";
import type { TrackMarker } from "@/content/types";

/**
 * Waveform supports two peaks formats:
 * - v1 (legacy): number[] — one absolute magnitude per bucket, in [0, 1].
 *   Rendered as a symmetric bar around the midline.
 * - v2 (current): [number, number][] — [min, max] signed pair per bucket, in
 *   [-1, 1] after percentile normalization. Rendered as an asymmetric bar
 *   showing the real wave envelope.
 */
export type PeaksData = number[] | Array<[number, number]>;

interface Props {
  peaks: PeaksData | null;
  duration: number;
  currentTime: number;
  markers?: TrackMarker[];
  onSeek: (sec: number) => void;
  height?: number;
}

function isSignedFormat(peaks: PeaksData): peaks is Array<[number, number]> {
  return Array.isArray(peaks[0]);
}

/**
 * Canvas-based waveform: bars symmetric around the midline, colored to indicate
 * played vs. unplayed using the EPK design palette. Click anywhere to seek.
 * Markers render as dashed overlays with labels above.
 *
 * `peaks` is a normalized [0, 1] array (see scripts/generate-peaks.mjs). When
 * null (e.g. mp3 previews we skip pre-rendering for), the component shows a
 * thin baseline so layout stays consistent.
 */
export function Waveform({
  peaks,
  duration,
  currentTime,
  markers = [],
  onSeek,
  height = 72,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);

    const midY = rect.height / 2;
    const progress = duration > 0 ? currentTime / duration : 0;
    const progressX = progress * rect.width;

    if (peaks && peaks.length > 0) {
      // Bars: 2px wide, 1px gap; bucket peaks into the available bar slots.
      const barWidth = 2;
      const gap = 1;
      const slot = barWidth + gap;
      const barCount = Math.max(1, Math.floor(rect.width / slot));
      const stride = peaks.length / barCount;
      const usableHalf = midY - 2;
      const signed = isSignedFormat(peaks);

      for (let i = 0; i < barCount; i++) {
        const start = Math.floor(i * stride);
        const end = Math.floor((i + 1) * stride);
        let topMag = 0;  // distance above midY (0..usableHalf)
        let botMag = 0;  // distance below midY (0..usableHalf)

        if (signed) {
          const sp = peaks as Array<[number, number]>;
          for (let j = start; j < end && j < sp.length; j++) {
            const pair = sp[j] ?? [0, 0];
            const mn = pair[0]; // negative
            const mx = pair[1]; // positive
            if (mx > topMag) topMag = mx;
            if (-mn > botMag) botMag = -mn;
          }
        } else {
          const lp = peaks as number[];
          let peak = 0;
          for (let j = start; j < end && j < lp.length; j++) {
            const v = lp[j] ?? 0;
            if (v > peak) peak = v;
          }
          topMag = peak;
          botMag = peak;
        }

        const yTop = midY - Math.max(1, topMag * usableHalf);
        const yBottom = midY + Math.max(1, botMag * usableHalf);
        const x = i * slot;
        const isPlayed = x + barWidth <= progressX;
        ctx.fillStyle = isPlayed ? "#504ddc" : "#7fade6";
        ctx.fillRect(x, yTop, barWidth, yBottom - yTop);
      }
    } else {
      // Fallback: thin baseline so the player area doesn't collapse.
      ctx.fillStyle = "#314361";
      ctx.fillRect(0, midY - 1, rect.width, 2);
    }

    // Playhead
    if (progress > 0 && progress <= 1) {
      ctx.strokeStyle = "#cceff4";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(progressX, 2);
      ctx.lineTo(progressX, rect.height - 2);
      ctx.stroke();
    }
  }, [peaks, currentTime, duration]);

  function handleClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    onSeek(ratio * duration);
  }

  return (
    <div style={{ position: "relative", paddingTop: 18 }}>
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        aria-label="Waveform — click to seek"
        style={{
          width: "100%",
          height,
          cursor: peaks ? "pointer" : "default",
          display: "block",
        }}
      />
      {markers.map((m, i) => {
        if (duration <= 0) return null;
        const leftPct = Math.max(0, Math.min(100, (m.atSec / duration) * 100));
        return (
          <button
            key={`${m.label}-${i}`}
            type="button"
            onClick={() => onSeek(m.atSec)}
            aria-label={`Seek to ${m.label}`}
            style={{
              position: "absolute",
              left: `${leftPct}%`,
              top: 0,
              bottom: 0,
              width: 0,
              borderLeft: "1px dashed #ff66e8",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              pointerEvents: "auto",
              outline: "none",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 0,
                left: 4,
                fontSize: 10,
                lineHeight: "14px",
                color: "#ff66e8",
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {m.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
